import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Button, Badge, Form, FormGroup, Label, Input, Alert, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMap, 
    faSync, 
    faExpand, 
    faCompress, 
    faSearchPlus, 
    faSearchMinus, 
    faUndo, 
    faTractor, 
    faExclamationTriangle, 
    faClock, 
    faUser, 
    faMapMarkerAlt,
    faThLarge,
    faList,
    faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-multi-lang';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import QuarriesService from '../Services/QuarriesService';
import CheckInsService from '../Services/CheckInsService';
import { MachinesService } from '../Services/MachinesService';
import FaultsService from '../Services/FaultsService';
import AuthService from '../Services/AuthService';
import { useFullscreen } from '../Contexts/FullscreenContext';
import { DateTime } from 'luxon';

const LiveMachineMap = () => {
    const t = useTranslation();
    const { isFullscreen, toggleFullscreen, setIsFullscreen } = useFullscreen();

    const [quarries, setQuarries] = useState([]);
    const [activeQuarry, setActiveQuarry] = useState(null);
    const [mapImage, setMapImage] = useState(null);
    const [activeCheckIns, setActiveCheckIns] = useState([]);
    const [machines, setMachines] = useState([]);
    const [activeFaults, setActiveFaults] = useState([]);
    const [currentTime, setCurrentTime] = useState(DateTime.now());
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Filters
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [selectedMachine, setSelectedMachine] = useState('');
    const [selectedShelf, setSelectedShelf] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Canvas Map state
    const canvasRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const quarriesService = useRef(new QuarriesService()).current;
    const checkInsService = useRef(new CheckInsService()).current;
    const machinesService = useRef(new MachinesService()).current;
    const faultsService = useRef(new FaultsService()).current;
    const authService = useRef(new AuthService()).current;
    const currentConfig = authService.getConfig();

    // Clean up fullscreen on unmount
    useEffect(() => {
        return () => {
            setIsFullscreen(false);
        };
    }, [setIsFullscreen]);

    // Timer for live duration calculation
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(DateTime.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Initial Load
    useEffect(() => {
        loadQuarries();
        loadMachines();
        fetchLiveData();
    }, []);

    // Auto-refresh data every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchLiveData(false);
        }, 10000);
        return () => clearInterval(interval);
    }, [activeQuarry]);

    const loadQuarries = async () => {
        try {
            const res = await quarriesService.getQuarries();
            setQuarries(res.data);
            if (currentConfig && currentConfig.whCode) {
                const matched = res.data.find(q => q.whCode === currentConfig.whCode);
                if (matched) {
                    setActiveQuarry(matched);
                } else if (res.data.length > 0) {
                    setActiveQuarry(res.data[0]);
                }
            } else if (res.data.length > 0) {
                setActiveQuarry(res.data[0]);
            }
        } catch (e) {
            console.error('Error loading quarries:', e);
        }
    };

    const loadMachines = async () => {
        try {
            const data = await machinesService.getMachines();
            setMachines(data || []);
        } catch (e) {
            console.error('Error loading machines:', e);
        }
    };

    const fetchLiveData = async (showLoading = true) => {
        if (showLoading) setIsRefreshing(true);
        try {
            const whCode = activeQuarry?.whCode || currentConfig?.whCode;
            const [checkInsData, faultsData] = await Promise.all([
                checkInsService.getActiveCheckIns(whCode),
                faultsService.getFaults({ resolved: false })
            ]);
            setActiveCheckIns(checkInsData || []);
            setActiveFaults(faultsData || []);
            setLastRefreshed(new Date());
        } catch (e) {
            console.error('Error fetching live check-ins or faults:', e);
        } finally {
            if (showLoading) setIsRefreshing(false);
        }
    };

    // Load Map Image when activeQuarry changes
    useEffect(() => {
        if (activeQuarry && activeQuarry.mapUrl) {
            const img = new Image();
            img.src = `${quarriesService.api.defaults.baseURL}quarries/map/${activeQuarry.id}`;
            img.onload = () => {
                setMapImage(img);
                setZoom(1);
                setOffset({ x: 0, y: 0 });
            };
            img.onerror = () => {
                setMapImage(null);
            };
        } else {
            setMapImage(null);
        }
    }, [activeQuarry]);

    // Categories & Subcategories
    const categories = Array.from(new Set(machines.map(m => m.category).filter(Boolean)));
    const subcategories = Array.from(
        new Set(
            machines
                .filter(m => !selectedCategory || m.category === selectedCategory)
                .map(m => m.subcategory)
                .filter(Boolean)
        )
    );

    // Enriched CheckIns
    const enrichedCheckIns = activeCheckIns.map(checkIn => {
        const machine = machines.find(m => m.code === checkIn.machineId);
        const fault = activeFaults.find(f => f.machineId === checkIn.machineId && !f.resolved);
        return {
            ...checkIn,
            machine,
            fault
        };
    });

    // Filtered CheckIns
    const filteredCheckIns = enrichedCheckIns.filter(checkIn => {
        const machine = checkIn.machine;
        if (selectedCategory && machine?.category !== selectedCategory) return false;
        if (selectedSubcategory && machine?.subcategory !== selectedSubcategory) return false;
        if (selectedMachine && checkIn.machineId !== selectedMachine) return false;
        if (selectedShelf && checkIn.area !== selectedShelf) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const mCode = (checkIn.machineId || '').toLowerCase();
            const mName = (machine?.name || '').toLowerCase();
            const uName = (checkIn.user?.name || '').toLowerCase();
            const sArea = (checkIn.area || '').toLowerCase();
            if (!mCode.includes(term) && !mName.includes(term) && !uName.includes(term) && !sArea.includes(term)) {
                return false;
            }
        }
        return true;
    });

    const getMachineStatus = (checkIn) => {
        if (checkIn.fault) return { text: t('machineMap.damage') || 'Βλάβη', color: '#dc3545', badgeColor: 'danger' };
        if (checkIn.status === 'work') return { text: t('machineMap.work') || 'Εργασία', color: '#28a745', badgeColor: 'success' };
        return { text: t('machineMap.waiting') || 'Αναμονή', color: '#ffc107', badgeColor: 'warning' };
    };

    const getElapsedTime = (dateStr) => {
        if (!dateStr) return '';
        const diff = currentTime.diff(DateTime.fromISO(dateStr), ['hours', 'minutes', 'seconds']).toObject();
        const hrs = Math.floor(diff.hours || 0);
        const mins = Math.floor(diff.minutes || 0);
        const secs = Math.floor(diff.seconds || 0);
        if (hrs > 0) return `${hrs}h ${mins}m`;
        return `${mins}m ${secs}s`;
    };

    // Draw Canvas Map
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!mapImage) {
            ctx.fillStyle = '#1e293b';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#94a3b8';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(t('machineMap.noMapUploaded') || 'Δεν έχει μεταφορτωθεί χάρτης για αυτό το λατομείο', canvas.width / 2, canvas.height / 2);
            return;
        }

        ctx.save();
        ctx.translate(offset.x, offset.y);
        ctx.scale(zoom, zoom);

        // Draw Map Image
        ctx.drawImage(mapImage, 0, 0, canvas.width, canvas.height);

        // Draw Polygons for Shelves
        if (activeQuarry && activeQuarry.shelves) {
            activeQuarry.shelves.forEach(shelf => {
                if (shelf.polygon && shelf.polygon.length > 0) {
                    ctx.beginPath();
                    ctx.moveTo(shelf.polygon[0].x * canvas.width, shelf.polygon[0].y * canvas.height);
                    for (let i = 1; i < shelf.polygon.length; i++) {
                        ctx.lineTo(shelf.polygon[i].x * canvas.width, shelf.polygon[i].y * canvas.height);
                    }
                    ctx.closePath();
                    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
                    ctx.lineWidth = 2 / zoom;
                    ctx.stroke();
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
                    ctx.fill();

                    // Shelf Name Centroid
                    const cx = (shelf.polygon.reduce((sum, p) => sum + p.x, 0) / shelf.polygon.length) * canvas.width;
                    const cy = (shelf.polygon.reduce((sum, p) => sum + p.y, 0) / shelf.polygon.length) * canvas.height;
                    ctx.font = `bold ${Math.max(10, 14 / zoom)}px Arial`;
                    ctx.fillStyle = 'rgba(0,0,0,0.7)';
                    const shelfTextWidth = ctx.measureText(shelf.name).width;
                    ctx.fillRect(cx - shelfTextWidth / 2 - 4 / zoom, cy - 20 / zoom, shelfTextWidth + 8 / zoom, 16 / zoom);
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    ctx.fillText(shelf.name, cx, cy - 8 / zoom);
                }
            });
        }

        // Draw Machines
        filteredCheckIns.forEach(checkIn => {
            const shelf = activeQuarry?.shelves?.find(s => s.name === checkIn.area);
            let x, y;

            if (shelf && shelf.polygon && shelf.polygon.length > 0) {
                x = shelf.polygon.reduce((sum, p) => sum + p.x, 0) / shelf.polygon.length;
                y = shelf.polygon.reduce((sum, p) => sum + p.y, 0) / shelf.polygon.length;
            } else {
                return;
            }

            const canvasX = x * canvas.width;
            const canvasY = y * canvas.height;

            const status = getMachineStatus(checkIn);

            // Draw Marker Icon
            ctx.fillStyle = status.color;
            ctx.beginPath();
            ctx.arc(canvasX, canvasY, 12 / zoom, 0, 2 * Math.PI);
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2 / zoom;
            ctx.stroke();

            // Label Box
            const machineName = checkIn.machine?.name || '';
            const userName = checkIn.user?.name || '';
            const workTypeStr = checkIn.workType ? ` [${checkIn.workType}]` : '';
            const text = `${checkIn.machineId}${machineName ? ` (${machineName})` : ''} - ${userName}${workTypeStr}`;
            ctx.font = `bold ${Math.max(9, 12 / zoom)}px Arial`;
            const metrics = ctx.measureText(text);
            ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
            ctx.fillRect(canvasX - metrics.width / 2 - 4 / zoom, canvasY + 16 / zoom, metrics.width + 8 / zoom, 16 / zoom);
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.fillText(text, canvasX, canvasY + 28 / zoom);
        });

        ctx.restore();
    }, [mapImage, activeQuarry, filteredCheckIns, zoom, offset, t]);

    useEffect(() => {
        draw();
    }, [draw]);

    const handleWheel = (e) => {
        e.preventDefault();
        const scaleFactor = 1.1;
        const delta = e.deltaY > 0 ? 1 / scaleFactor : scaleFactor;
        const newZoom = zoom * delta;
        if (newZoom >= 0.8 && newZoom <= 10) {
            const rect = canvasRef.current.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const newOffsetX = mouseX - (mouseX - offset.x) * delta;
            const newOffsetY = mouseY - (mouseY - offset.y) * delta;
            setZoom(newZoom);
            setOffset({ x: newOffsetX, y: newOffsetY });
        }
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setOffset({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const resetView = () => {
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    };

    const activeCount = filteredCheckIns.length;
    const workingCount = filteredCheckIns.filter(c => c.status === 'work' && !c.fault).length;
    const waitingCount = filteredCheckIns.filter(c => c.status !== 'work' && !c.fault).length;
    const damageCount = filteredCheckIns.filter(c => !!c.fault).length;

    return (
        <div className={`live-machine-screen ${isFullscreen ? 'p-3 bg-dark text-white' : 'p-3'}`}>
            {/* Top Toolbar */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                    <h3 className="mb-0 fw-bold d-flex align-items-center">
                        <FontAwesomeIcon icon={faMap} className="text-primary me-2" />
                        {t('machineMap.splitViewTitle') || 'Χάρτης & Λίστα Μηχανημάτων (Live)'}
                    </h3>
                    {activeQuarry && (
                        <Badge color="info" className="px-2 py-1 fs-6">
                            {activeQuarry.name}
                        </Badge>
                    )}
                </div>

                {/* Status Badges & Refresh & Fullscreen Switch */}
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <div className="d-flex gap-2">
                        <Badge color="primary" className="p-2 fs-6">
                            {t('machineMap.totalActive') || 'Σύνολο'}: {activeCount}
                        </Badge>
                        <Badge color="success" className="p-2 fs-6">
                            {t('machineMap.work') || 'Εργασία'}: {workingCount}
                        </Badge>
                        <Badge color="warning" className="p-2 fs-6 text-dark">
                            {t('machineMap.waiting') || 'Αναμονή'}: {waitingCount}
                        </Badge>
                        <Badge color="danger" className="p-2 fs-6">
                            {t('machineMap.damage') || 'Βλάβη'}: {damageCount}
                        </Badge>
                    </div>

                    <Button 
                        color="secondary" 
                        size="sm" 
                        onClick={() => fetchLiveData(true)}
                        disabled={isRefreshing}
                        title={t('refresh') || 'Ανανέωση'}
                    >
                        <FontAwesomeIcon icon={faSync} spin={isRefreshing} className="me-1" />
                        {DateTime.fromJSDate(lastRefreshed).setZone('Europe/Athens').toFormat('HH:mm:ss')}
                    </Button>

                    <Button 
                        color={isFullscreen ? "danger" : "primary"} 
                        size={isFullscreen ? "md" : "sm"}
                        onClick={toggleFullscreen}
                        className={isFullscreen ? "fw-bold shadow" : "fw-bold"}
                        title={isFullscreen ? (t('fullscreen.exit') || "Κανονική Προβολή") : (t('fullscreen.enter') || "Πλήρης Οθόνη")}
                    >
                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} className="me-1" />
                        {isFullscreen ? (t('fullscreen.exit') || 'Κανονική Προβολή') : (t('fullscreen.enter') || 'Πλήρης Οθόνη')}
                    </Button>
                </div>
            </div>

            {/* Filter Bar */}
            <Card className={`mb-3 shadow-sm ${isFullscreen ? 'bg-secondary text-white' : ''}`}>
                <CardBody className="py-2">
                    <Row className="g-2 align-items-center">
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={activeQuarry ? activeQuarry.id : ''} 
                                onChange={(e) => {
                                    const q = quarries.find(item => item.id.toString() === e.target.value);
                                    setActiveQuarry(q);
                                }}
                                className="form-select-sm"
                            >
                                {quarries.map(q => (
                                    <option key={q.id} value={q.id}>{q.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={selectedCategory} 
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                    setSelectedSubcategory('');
                                }}
                                className="form-select-sm"
                            >
                                <option value="">{t('machineMap.allCategories') || 'Όλες οι Κατηγορίες'}</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={selectedSubcategory} 
                                onChange={(e) => setSelectedSubcategory(e.target.value)}
                                className="form-select-sm"
                                disabled={!selectedCategory}
                            >
                                <option value="">{t('machineMap.allSubcategories') || 'Όλες οι Υποκατηγορίες'}</option>
                                {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={selectedShelf} 
                                onChange={(e) => setSelectedShelf(e.target.value)}
                                className="form-select-sm"
                            >
                                <option value="">{t('machineMap.allShelves') || 'Όλα τα Πατάρια'}</option>
                                {activeQuarry?.shelves?.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={4}>
                            <Input 
                                type="text" 
                                placeholder={t('machineMap.searchPlaceholder') || 'Αναζήτηση μηχανήματος, οδηγού...'} 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-control-sm"
                            />
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Split Screen Content: Map (Left) and List (Right) */}
            <Row className="g-3">
                {/* Left Column: Map */}
                <Col lg={7} xl={7}>
                    <Card className={`shadow-sm h-100 ${isFullscreen ? 'bg-secondary text-white border-0' : ''}`}>
                        <CardHeader className="d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">
                                <FontAwesomeIcon icon={faMap} className="me-2 text-primary" />
                                {t('machineMap.mapView') || 'Χάρτης Εγκατάστασης'}
                            </span>
                            <div className="d-flex gap-1">
                                <Button size="sm" color="light" onClick={() => setZoom(z => Math.min(z * 1.2, 10))} title="Zoom In">
                                    <FontAwesomeIcon icon={faSearchPlus} />
                                </Button>
                                <Button size="sm" color="light" onClick={() => setZoom(z => Math.max(z / 1.2, 0.8))} title="Zoom Out">
                                    <FontAwesomeIcon icon={faSearchMinus} />
                                </Button>
                                <Button size="sm" color="light" onClick={resetView} title="Reset View">
                                    <FontAwesomeIcon icon={faUndo} />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody className="p-0 position-relative d-flex justify-content-center align-items-center bg-dark rounded-bottom overflow-hidden">
                            {mapImage ? (
                                <canvas
                                    ref={canvasRef}
                                    width={1000}
                                    height={isFullscreen ? 750 : 550}
                                    style={{ width: '100%', height: isFullscreen ? 'calc(100vh - 220px)' : '550px', cursor: isDragging ? 'grabbing' : 'grab' }}
                                    onWheel={handleWheel}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                />
                            ) : (
                                <div className="p-4 text-center w-100" style={{ height: isFullscreen ? 'calc(100vh - 220px)' : '550px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Alert color="info" className="d-inline-block">
                                        <h5>{activeQuarry ? activeQuarry.name : t('quarries.selectQuarry')}</h5>
                                        <p className="mb-0">{t('machineMap.noMapAvailable') || 'Δεν έχει φορτωθεί αρχείο χάρτη. Παρακάτω προβάλλονται τα μηχανήματα ανά πατάρι.'}</p>
                                    </Alert>
                                    {/* Shelf Grid fallback */}
                                    <Row className="g-2 text-start p-2 overflow-auto" style={{ maxHeight: '400px' }}>
                                        {activeQuarry?.shelves?.map(shelf => {
                                            const shelfMachines = filteredCheckIns.filter(c => c.area === shelf.name);
                                            return (
                                                <Col key={shelf.id} md={6}>
                                                    <Card className="p-2 bg-slate-800 text-white border-secondary">
                                                        <div className="fw-bold text-info mb-1">{shelf.name} ({shelfMachines.length})</div>
                                                        {shelfMachines.length === 0 && <small className="text-muted">{t('machineMap.noMachines') || 'Κανένα μηχάνημα'}</small>}
                                                        {shelfMachines.map(c => {
                                                            const st = getMachineStatus(c);
                                                            return (
                                                                <div key={c.id} className="d-flex justify-content-between align-items-center py-1 border-bottom border-dark">
                                                                    <span><strong>{c.machineId}</strong> {c.machine?.name} ({c.user?.name})</span>
                                                                    <Badge color={st.badgeColor}>{st.text}</Badge>
                                                                </div>
                                                            );
                                                        })}
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}

                            {/* Legend Overlay */}
                            <div className="position-absolute bottom-0 start-0 m-2 p-2 bg-dark bg-opacity-75 rounded text-white small d-flex gap-3">
                                <div><span className="badge bg-success me-1">●</span> {t('machineMap.work') || 'Εργασία'}</div>
                                <div><span className="badge bg-warning text-dark me-1">●</span> {t('machineMap.waiting') || 'Αναμονή'}</div>
                                <div><span className="badge bg-danger me-1">●</span> {t('machineMap.damage') || 'Βλάβη'}</div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* Right Column: Live Machines List */}
                <Col lg={5} xl={5}>
                    <Card className={`shadow-sm h-100 ${isFullscreen ? 'bg-secondary text-white border-0' : ''}`}>
                        <CardHeader className="d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">
                                <FontAwesomeIcon icon={faList} className="me-2 text-primary" />
                                {t('machineMap.liveMachinesList') || 'Λίστα Ενεργών Μηχανημάτων'} ({filteredCheckIns.length})
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <ReactHTMLTableToExcel
                                    id="liveMachinesExcelBtn"
                                    className="btn btn-sm btn-success fw-bold"
                                    table="liveMachinesExportTable"
                                    filename={`live_machines_${activeQuarry?.name || 'quarry'}_${DateTime.now().setZone('Europe/Athens').toFormat('yyyyMMdd_HHmm')}`}
                                    sheet="LiveMachines"
                                    buttonText="Excel"
                                />
                            </div>
                        </CardHeader>
                        <CardBody className="p-0 overflow-auto" style={{ maxHeight: isFullscreen ? 'calc(100vh - 220px)' : '550px' }}>
                            {filteredCheckIns.length === 0 ? (
                                <div className="p-4 text-center text-muted">
                                    {t('machineMap.noActiveCheckIns') || 'Δεν βρέθηκαν ενεργά μηχανήματα με τα επιλεγμένα κριτήρια.'}
                                </div>
                            ) : (
                                <Table id="liveMachinesTable" responsive hover striped className={`mb-0 align-middle ${isFullscreen ? 'table-dark' : ''}`}>
                                    <thead className="table-light text-dark sticky-top">
                                        <tr>
                                            <th>{t('machineMap.machine') || 'Μηχάνημα'}</th>
                                            <th>{t('machineMap.shelf') || 'Πατάρι'}</th>
                                            <th>{t('machineMap.driver') || 'Χειριστής'}</th>
                                            <th>{t('machineMap.status') || 'Κατάσταση'}</th>
                                            <th>{t('machineMap.duration') || 'Διάρκεια'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCheckIns.map(checkIn => {
                                            const status = getMachineStatus(checkIn);
                                            const elapsed = getElapsedTime(checkIn.createdAt);

                                            return (
                                                <tr key={checkIn.id}>
                                                    <td>
                                                        <div className="fw-bold d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faTractor} className="me-1 text-secondary" />
                                                            {checkIn.machineId}
                                                        </div>
                                                        {checkIn.machine?.name && (
                                                            <small className="text-muted d-block">{checkIn.machine.name}</small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Badge color="info" className="text-dark">
                                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                                            {checkIn.area || '-'}
                                                        </Badge>
                                                        {checkIn.workType && (
                                                            <small className="d-block text-primary fw-bold mt-1">
                                                                {checkIn.workType}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center small">
                                                            <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                                                            {checkIn.user?.name || '-'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge color={status.badgeColor} className="p-1 px-2">
                                                            {checkIn.fault && <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />}
                                                            {status.text}
                                                        </Badge>
                                                        {checkIn.fault && (
                                                            <small className="d-block text-danger mt-1 fw-bold">
                                                                {checkIn.fault.reason}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="small text-nowrap">
                                                            <FontAwesomeIcon icon={faClock} className="me-1 text-muted" />
                                                            {elapsed}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}

                            {/* Hidden Table for Clean Excel Export */}
                            <Table id="liveMachinesExportTable" className="d-none">
                                <thead>
                                    <tr>
                                        <th>{t('machineMap.machine') || 'Μηχάνημα'}</th>
                                        <th>{t('machines.description') || 'Περιγραφή'}</th>
                                        <th>{t('machineMap.category') || 'Κατηγορία'}</th>
                                        <th>{t('machineMap.subcategory') || 'Υποκατηγορία'}</th>
                                        <th>{t('machineMap.shelf') || 'Πατάρι'}</th>
                                        <th>{t('machineTypes.workTypeName') || 'Εργασία'}</th>
                                        <th>{t('machineMap.driver') || 'Χειριστής'}</th>
                                        <th>{t('machineMap.status') || 'Κατάσταση'}</th>
                                        <th>{t('machineMap.faultReason') || 'Αιτία Βλάβης'}</th>
                                        <th>{t('machineMap.checkInTime') || 'Ώρα Έναρξης'}</th>
                                        <th>{t('machineMap.duration') || 'Διάρκεια'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCheckIns.map(checkIn => {
                                        const status = getMachineStatus(checkIn);
                                        const elapsed = getElapsedTime(checkIn.createdAt);
                                        const checkInFormatted = checkIn.createdAt ? DateTime.fromISO(checkIn.createdAt).setZone('Europe/Athens').toFormat('dd/MM/yyyy HH:mm:ss') : '-';
                                        return (
                                            <tr key={`export-${checkIn.id}`}>
                                                <td>{checkIn.machineId || ''}</td>
                                                <td>{checkIn.machine?.name || ''}</td>
                                                <td>{checkIn.machine?.category || ''}</td>
                                                <td>{checkIn.machine?.subcategory || ''}</td>
                                                <td>{checkIn.area || '-'}</td>
                                                <td>{checkIn.workType || '-'}</td>
                                                <td>{checkIn.user?.name || '-'}</td>
                                                <td>{status.text}</td>
                                                <td>{checkIn.fault ? checkIn.fault.reason || '-' : '-'}</td>
                                                <td>{checkInFormatted}</td>
                                                <td>{elapsed}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default LiveMachineMap;
